import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit, Trash2, X, Star } from 'lucide-react';
import {
  fetchTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial
} from '../../lib/sanityClient';
import ConfirmDialog from './ConfirmDialog';
import SuccessToast from './SuccessToast';
import ErrorAlert from './ErrorAlert';

export default function AdminTestimonials({ refreshTrigger = 0 }) {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null });
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    rating: '5',
    text: ''
  });
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    loadTestimonials();
  }, [refreshTrigger]);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const data = await fetchTestimonials();
      setTestimonials(data || []);
      setError('');
    } catch (err) {
      setError('Kunne ikke laste kundeomtaler');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingId(null);
    setFormData({
      name: '',
      title: '',
      rating: '5',
      text: ''
    });
    setShowForm(true);
  };

  const handleEdit = (testimonial) => {
    setEditingId(testimonial._id);
    setFormData({
      name: testimonial.name,
      title: testimonial.title,
      rating: String(testimonial.rating),
      text: testimonial.text
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      // Validate
      if (!formData.name.trim() || !formData.title.trim() || !formData.text.trim()) {
        setError('Alle felt må fylles ut');
        setSubmitLoading(false);
        return;
      }

      if (editingId) {
        await updateTestimonial(editingId, formData);
        setSuccessMessage('Kundeomtale oppdatert!');
      } else {
        await createTestimonial(formData);
        setSuccessMessage('Kundeomtale lagt til!');
      }

      setShowForm(false);
      await loadTestimonials();
      setError('');
    } catch (err) {
      setError(err.message || 'En feil oppstod');
      console.error(err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setSubmitLoading(true);
      await deleteTestimonial(confirmDialog.id);
      setSuccessMessage('Kundeomtale slettet!');
      setConfirmDialog({ isOpen: false, id: null });
      await loadTestimonials();
    } catch (err) {
      setError(err.message || 'Kunne ikke slette kundeomtale');
      console.error(err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-Pink-main"></div>
      </div>
    );
  }

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <ErrorAlert message={error} onClose={() => setError('')} />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-Neautral-main font-opensans">
          Kundeomtaler ({testimonials.length})
        </h2>
        <motion.button
          onClick={handleAddNew}
          className="flex items-center justify-center gap-2 px-6 py-3 rounded-[15px] bg-Pink-main text-white font-semibold font-opensans hover:opacity-90 transition w-full sm:w-auto"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={20} strokeWidth={2} />
          Ny Omtale
        </motion.button>
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            className="mb-8 bg-Pink-BG rounded-[15px] p-4 sm:p-6 md:p-8 border-2 border-Pink-main"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            variants={itemVariants}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-Neautral-main font-opensans">
                {editingId ? 'Rediger Kundeomtale' : 'Ny Kundeomtale'}
              </h3>
              <motion.button
                onClick={() => setShowForm(false)}
                className="text-Neautral-light hover:text-Neautral-main transition"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <X size={24} strokeWidth={2} />
              </motion.button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                  Navn *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Kundens navn"
                  className="w-full px-4 py-2 rounded-[10px] border-2 border-Neautral-base bg-Neautral-white text-Neautral-main placeholder-Neautral-light focus:outline-none focus:border-Pink-main transition font-opensans"
                />
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                  Tittel/Yrke *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="f.eks. Kundestylist"
                  className="w-full px-4 py-2 rounded-[10px] border-2 border-Neautral-base bg-Neautral-white text-Neautral-main placeholder-Neautral-light focus:outline-none focus:border-Pink-main transition font-opensans"
                />
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                  Rating *
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: String(star) })}
                      className={`p-2 rounded-[8px] transition ${
                        parseInt(formData.rating) >= star
                          ? 'bg-Pink-main'
                          : 'bg-Neautral-base hover:bg-Neautral-light'
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Star
                        size={24}
                        className={
                          parseInt(formData.rating) >= star
                            ? 'text-white'
                            : 'text-Neautral-light'
                        }
                        fill={parseInt(formData.rating) >= star ? 'currentColor' : 'none'}
                        strokeWidth={1.5}
                      />
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Text */}
              <div>
                <label className="block text-sm font-semibold text-Neautral-main font-opensans mb-2">
                  Omtale *
                </label>
                <textarea
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  placeholder="Skriv kundeomtalen her..."
                  rows="4"
                  className="w-full px-4 py-2 rounded-[10px] border-2 border-Neautral-base bg-Neautral-white text-Neautral-main placeholder-Neautral-light focus:outline-none focus:border-Pink-main transition font-opensans resize-none"
                />
              </div>

              {/* Buttons */}
              <div className="flex flex-col-reverse sm:flex-row gap-4 justify-end pt-4">
                <motion.button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={submitLoading}
                  className="px-6 py-2 rounded-[10px] bg-Neautral-base text-Neautral-main font-semibold font-opensans hover:bg-Neautral-light transition disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Avbryt
                </motion.button>
                <motion.button
                  type="submit"
                  disabled={submitLoading}
                  className="px-6 py-2 rounded-[10px] bg-Pink-main text-white font-semibold font-opensans hover:opacity-90 transition disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {submitLoading ? 'Lagrer...' : editingId ? 'Oppdater' : 'Opprett'}
                </motion.button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Testimonials List */}
      {testimonials.length === 0 ? (
        <motion.div
          className="text-center py-12 text-Neautral-light font-opensans"
          variants={itemVariants}
        >
          Ingen kundeomtaler ennå. Klikk "Ny Omtale" for å legge til en.
        </motion.div>
      ) : (
        <motion.div className="space-y-4" variants={containerVariants}>
          {testimonials.map((testimonial) => (
            <motion.div
              key={testimonial._id}
              className="bg-Pink-BG rounded-[15px] p-4 sm:p-6 border-2 border-Neautral-base hover:border-Pink-main transition"
              variants={itemVariants}
            >
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-Neautral-main font-opensans break-words">
                    {testimonial.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-Neautral-light font-opensans mb-2">
                    {testimonial.title}
                  </p>

                  {/* Rating Stars */}
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        className={i < testimonial.rating ? 'text-Pink-main' : 'text-Neautral-light'}
                        fill={i < testimonial.rating ? 'currentColor' : 'none'}
                        strokeWidth={1.5}
                      />
                    ))}
                  </div>

                  <p className="text-Neautral-main font-opensans text-xs sm:text-sm line-clamp-2">
                    {testimonial.text}
                  </p>
                </div>

                <div className="flex gap-2 flex-shrink-0">
                  <motion.button
                    onClick={() => handleEdit(testimonial)}
                    className="p-2 rounded-[10px] bg-Neautral-base text-Neautral-main hover:bg-Pink-main hover:text-white transition"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Edit size={16} strokeWidth={2} />
                  </motion.button>
                  <motion.button
                    onClick={() => setConfirmDialog({ isOpen: true, id: testimonial._id })}
                    className="p-2 rounded-[10px] bg-red-100 text-red-600 hover:bg-red-200 transition"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Trash2 size={16} strokeWidth={2} />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Slett kundeomtale?"
        message="Er du sikker på at du vil slette denne kundeomtalen? Denne handlingen kan ikke angres."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, id: null })}
        isLoading={submitLoading}
      />

      {/* Success Toast */}
      <SuccessToast
        message={successMessage}
        isVisible={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </motion.div>
  );
}
